import React from 'react';
import styled from '@emotion/native';
import { TouchableOpacity, View } from 'react-native';
import { BodyText } from '@/shared/components/StyledText';
import {
  accent,
  darkGreen,
  green,
  sand,
  secondaryHover,
  white,
} from '@/shared/styles/colors';
import { IS_WEB } from '@/shared/constants/utility';
import { useWebHoverToggle } from '@/shared/hooks/useWebHoverToggle';
import { FONT_STYLES, WELCOME_CTA_MAX_WIDTH } from '@/shared/styles/constants';
import { buttonDimensions } from '@/shared/styles/components/button/buttonStyles';

export interface WelcomeCtaProps {
  onPress: () => void;
  accessibilityLabel: string;
  accessibilityRole?: 'link' | 'button';
  alignSelf?: 'flex-start' | 'center' | 'flex-end' | 'stretch';
  /** Defaults to {@link WELCOME_CTA_MAX_WIDTH}. */
  maxWidth?: number;
  /** Sand (default), accent, or dark green fill with white label. */
  variant?: 'sand' | 'accent' | 'darkGreen';
  leadingIcon?: React.ReactNode;
  children: React.ReactNode;
}

const BUTTON_HEIGHT = buttonDimensions.large.height.mobile;

/** Matches accent control hover on welcome testimonials carousel. */
const ACCENT_HOVER = '#ebe86a';

type WelcomeCtaVariant = NonNullable<WelcomeCtaProps['variant']>;

interface StyledTouchableProps {
  $alignSelf: NonNullable<WelcomeCtaProps['alignSelf']>;
  $maxWidth: number;
  $variant: WelcomeCtaVariant;
}

interface LabelProps {
  $variant: WelcomeCtaVariant;
}

const WelcomeCta = ({
  onPress,
  accessibilityLabel,
  accessibilityRole = 'link',
  alignSelf = 'center',
  maxWidth = WELCOME_CTA_MAX_WIDTH,
  variant = 'sand',
  leadingIcon,
  children,
}: WelcomeCtaProps) => {
  const { isHovered, webHoverProps } = useWebHoverToggle();
  const hoverBackgroundColor =
    variant === 'accent'
      ? ACCENT_HOVER
      : variant === 'darkGreen'
        ? green
        : secondaryHover;

  return (
    <StyledTouchable
      onPress={onPress}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={accessibilityLabel}
      activeOpacity={0.85}
      {...webHoverProps}
      style={
        IS_WEB && isHovered
          ? { backgroundColor: hoverBackgroundColor }
          : undefined
      }
      $alignSelf={alignSelf}
      $maxWidth={maxWidth}
      $variant={variant}
    >
      {leadingIcon ? (
        <LabelRow>
          <LeadingIconSlot
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
          >
            {leadingIcon}
          </LeadingIconSlot>
          <Label $variant={variant}>{children}</Label>
        </LabelRow>
      ) : (
        <Label $variant={variant}>{children}</Label>
      )}
    </StyledTouchable>
  );
};

const StyledTouchable = styled(TouchableOpacity)<StyledTouchableProps>(
  ({ $alignSelf, $maxWidth, $variant }) => ({
    alignSelf: $alignSelf,
    backgroundColor:
      $variant === 'accent'
        ? accent
        : $variant === 'darkGreen'
          ? darkGreen
          : sand,
    height: BUTTON_HEIGHT,
    width: '100%',
    maxWidth: $maxWidth,
    borderRadius: 999,
    justifyContent: 'center',
    alignItems: 'center',
  }),
);

const LabelRow = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
});

const LeadingIconSlot = styled(View)({
  marginRight: 8,
});

const Label = styled(BodyText)<LabelProps>(({ $variant }) => ({
  color: $variant === 'darkGreen' ? white : green,
  ...FONT_STYLES.welcomeHelveticaNeue,
  fontSize: 14,
  fontWeight: '700',
  letterSpacing: 0.5,
  marginBottom: 0,
}));

export default WelcomeCta;
